import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentReturnService } from "../../services/shipmentReturnService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { Plus, Filter, ArrowLeft, Pencil, Package, MoreVertical } from "lucide-react";
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { formatAuditUser } from '../../utils/userDisplay';


const ViewReturn = () => {
  const { t } = useTranslation();
  const { returnId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [returnData, setReturnData] = useState(null)
  const [shipmentStatus, setShipmentStatus] = useState(null)
  const [receiptPreview, setReceiptPreview] = useState(null)
  const [downloadingReceipt, setDownloadingReceipt] = useState(false)
  const [loadingReceipt, setLoadingReceipt] = useState(false)
  const [showReceiptModal, setShowReceiptModal] = useState(false)

  useEffect(() => {
    fetchReturn()
  }, [returnId])

  const fetchReturn = async () => {
    try {
      setLoading(true)
      const response = await shipmentReturnService.getReturn(returnId)
      const returnInfo = response.data.return || response.data.data

      if (returnInfo) {
        setReturnData(returnInfo)
        if (returnInfo.shipment_status) {
          setShipmentStatus(returnInfo.shipment_status)
        }
        if (returnInfo.has_payment_receipt) {
          fetchReceiptPreview()
        }
      } else {
        Alert.error("Return request not found")
      }
    } catch (error) {
      console.error("Error fetching return:", error)
      Alert.error(error.response?.data?.error || "Failed to load return request")
    } finally {
      setLoading(false)
    }
  }

  const fetchReceiptPreview = async () => {
    try {
      setLoadingReceipt(true)
      const response = await shipmentReturnService.getPaymentReceipt(returnId)

      // Convert blob to base64
      const reader = new FileReader()
      reader.onload = () => {
        const base64String = reader.result.split(",")[1]

        // Detect MIME type from blob
        const blob = response.data
        const mimeType = blob.type || "application/octet-stream"

        setReceiptPreview({
          receipt_base64: base64String,
          mime_type: mimeType,
          filename: `payment_receipt_${returnId}`,
        })
      }
      reader.readAsDataURL(response.data)
    } catch (error) {
      console.error("Error loading receipt preview:", error)
    } finally {
      setLoadingReceipt(false)
    }
  }

  const canEdit = ["System Admin", "Admin", "Accountant"].includes(user?.role)
  const isShipmentClosed = shipmentStatus === "closed" || shipmentStatus === "close"
  const canShowEditIcon = canEdit && returnData?.return_status === "open" && !isShipmentClosed

  const handleDownloadReceipt = async () => {
    try {
      setDownloadingReceipt(true)
      const response = await shipmentReturnService.getPaymentReceipt(returnData.return_id)
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", receiptPreview?.filename || "payment_receipt.pdf")
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      Alert.success("Receipt downloaded successfully!")
    } catch (error) {
      console.error("Error downloading receipt:", error)
      Alert.error(error.response?.data?.error || "Failed to download payment receipt")
    } finally {
      setDownloadingReceipt(false)
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "bg-yellow-100 text-yellow-800 border-yellow-200",
      close: "bg-green-100 text-green-800 border-green-200",
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "📖"
      case "close":
        return "✅"
      case "pending":
        return "⏳"
      case "approved":
        return "✅"
      case "rejected":
        return "❌"
      default:
        return "📋"
    }
  }

  const isPaymentInitiated = returnData?.payment_initiated?.approved || false
  const isPaymentRefunded = returnData?.payment_refunded?.approved || false

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading return request..." />
        </div>
      </DashboardLayout>
    )
  }

  if (!returnData) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 max-w-md text-center">
              <div className="text-6xl mb-4">❌</div>
              <p className="text-red-600 text-lg font-bold mb-2">Return Request Not Found</p>
              <p className="text-gray-600 mb-4">The return request you are looking for does not exist.</p>
              <Button variant="primary" onClick={() => navigate("/returns")}>
                Back to Returns
              </Button>
            </div>
        </div>
      </DashboardLayout>
    )
  }

  const ReceiptModal = () => {
    if (!showReceiptModal) return null
  
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="bg-primary px-6 py-4 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-secondary">Payment Receipt</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowReceiptModal(false)}
              className="text-secondary hover:bg-white/20"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
  
          <div className="flex-1 overflow-auto p-6">
            {loadingReceipt ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading receipt...</p>
                </div>
              </div>
            ) : receiptPreview ? (
              <div className="flex justify-center">
                <img
                  src={`data:${receiptPreview.mime_type};base64,${receiptPreview.receipt_base64}`}
                  alt="Payment Receipt"
                  className="max-w-full h-auto rounded-xl shadow-lg border-2 border-gray-200"
                />
              </div>
            ) : (
              <div className="text-center text-gray-500">No receipt available</div>
            )}
          </div>
  
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
            <Button variant="pagination" onClick={() => setShowReceiptModal(false)} className="flex-1">
              Close
            </Button>
            <Button variant="success" onClick={handleDownloadReceipt} disabled={downloadingReceipt} className="flex-1">
              {downloadingReceipt ? "Downloading..." : t("common.download")}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
    <DashboardLayout>
              <div className="max-w-7xl mx-auto">
                <div className="mb-6">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4 sm:mb-6">
                    <div>
                      <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                        <Package className="w-6 h-6 text-secondary" />Return Request Details</h1>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                      <div className={`flex items-center justify-center gap-2 px-3 sm:px-3 py-2 text-xs sm:text-sm text-secondary`}>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{getStatusIcon(returnData.return_status)}</span>
                            <div>
                              <p className="font-semibold text-sm uppercase">{returnData.return_status}</p>
                              <p className="text-xs mt-0">
                                {returnData.return_status?.toLowerCase() === "open" && "Awaiting payment processing"}
                                {returnData.return_status?.toLowerCase() === "close" && "Return request completed"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <Button variant="primary" onClick={() => navigate(`/shipments/view/${returnData.shipment_id}`)}>
                        View Shipment
                      </Button>
                      <Button variant="secondary" icon={ArrowLeft} onClick={() => navigate("/returns")}>
                        Back to Returns
                      </Button>
                  </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl overflow-hidden mb-6">
                  <div className="bg-white px-4 sm:px-6 py-4">
                    <h3 className="text-base sm:text-md font-semibold text-secondary mb-0 sm:mb-0 pb-2 border-b-2 border-grey-400">
                      Return Information
                    </h3>
                  </div>

                  <div className="p-4 sm:p-6 sm:pt-0">
                    <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 sm:gap-4">
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Return ID</p>
                        <p className="font-medium text-gray-700 text-sm sm:text-base p-0 py-1 mt-1">#{returnData.return_id}</p>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Shipment ID</p>
                        <Button variant="link" onClick={() => navigate(`/shipments/view/${returnData.shipment_id}`)} className="font-medium text-secondary text-sm sm:text-base p-0 py-1 mt-1">
                          #{returnData.shipment_id}
                        </Button>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">{t('common.createdBy')}</p>
                        <p className="font-medium text-gray-700 text-sm sm:text-base p-0 py-1 mt-1">
                          {formatAuditUser(returnData, 'created_by')}
                        </p>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Created Date</p>
                        <p className="font-medium text-gray-700 text-sm sm:text-base p-0 py-1 mt-1">
                          {returnData.created_at
                            ? new Date(returnData.created_at).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/A"}
                        </p>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-2">Return Status</p>
                        <span
                          className="font-medium text-gray-700 text-sm sm:text-base p-0 py-1 mt-4"
                        >
                          {returnData.return_status}
                        </span>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold">Last Updated</p>
                        <p className="font-medium text-gray-700 text-sm sm:text-base p-0 py-1 mt-2">
                          {returnData.updated_at
                            ? new Date(returnData.updated_at).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl mb-6">
                  <div className="bg-white px-4 sm:px-6 py-4">
                    <h3 className="text-base sm:text-md font-semibold text-secondary mb-0 sm:mb-0 pb-2 border-b-2 border-grey-400">
                      Reason for Return
                    </h3>
                  </div>
                  <div className="p-4 sm:p-6 sm:pt-0">
                    <div className="p-0">
                      <p className="font-medium text-gray-700 text-sm sm:text-base p-0 py-0 mt-0">{returnData.reason_for_return}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Timeline */}
                <div className="bg-white rounded-xl mb-6">
                  <div className="bg-white px-4 sm:px-0 py-4 flex items-center justify-between border-b-2 border-color-gray mx-6">
  
                  {/* LEFT TITLE */}
                  <h3 className="text-base sm:text-md font-semibold text-secondary mb-0 pb-0">
                    Payment Timeline
                  </h3>

                  {/* RIGHT BUTTON */}
                  <Button variant="primary" onClick={() => setShowReceiptModal(true)}>
                    View Payment Receipt
                  </Button>

                </div>

                  <div className="p-4 sm:p-6 space-y-6">
                    {/* Payment Initiated */}
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                            isPaymentInitiated ? "bg-green-500" : "bg-gray-300"
                          }`}
                        >
                          1
                        </div>
                        <div className="w-1 h-12 bg-gray-300 my-1"></div>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-secondary">Payment Initiated</p>
                        {isPaymentInitiated ? (
                          <div className="text-sm text-gray-600 mt-1">
                            <p>By: {returnData.payment_initiated?.initiated_by?.user_name}</p>
                            <p>
                              {returnData.payment_initiated?.timestamp
                                ? new Date(returnData.payment_initiated.timestamp).toLocaleString("en-IN")
                                : "N/A"}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 mt-1">Pending</p>
                        )}
                      </div>
                    </div>

                    {/* Payment Refunded */}
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                            isPaymentRefunded ? "bg-green-500" : "bg-gray-300"
                          }`}
                        >
                          2
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-secondary">Payment Refunded</p>
                        {isPaymentRefunded ? (
                          <div className="text-sm text-gray-600 mt-1">
                            <p>By: {returnData.payment_refunded?.initiated_by?.user_name}</p>
                            <p>
                              {returnData.payment_refunded?.timestamp
                                ? new Date(returnData.payment_refunded.timestamp).toLocaleString("en-IN")
                                : "N/A"}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 mt-1">Pending</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
    </DashboardLayout>
    <ReceiptModal />
    </>
  )
}

export default ViewReturn;